/**
 * Choose Options modal controller.
 *
 * Opens the per-product variant picker modal rendered by
 * snippets/product-choose-options.liquid. Variant matching, price updates and
 * the actual "Add to cart" submission are all handled by the theme's existing
 * jQuery handlers (see theme.js: changeSwatchQuickShop / initAddToCart) which
 * already listen on [data-quickshop] / [data-btn-addtocart] anywhere on the
 * page — this file only owns opening/closing the dialog itself.
 *
 * Plain vanilla JS, event-delegated on document, so it works for cards
 * rendered after page load too (infinite scroll, filtered collections, etc.).
 */
(function () {
    'use strict';

    var ACTIVE_CLASS = 'is-open';
    var BODY_LOCK_CLASS = 'choose-options-open';
    var FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    var activeOverlay = null;
    var lastTrigger = null;
    var releasedAncestors = [];

    function getOverlay(trigger) {
        var targetId = trigger.getAttribute('data-choose-options-target');
        if (!targetId) return null;
        return document.getElementById(targetId);
    }

    // Any ancestor with a `transform` (or filter/perspective/will-change:
    // transform) becomes the *positioning containing block* for a `position:
    // fixed` descendant instead of the viewport — Slick-powered sliders
    // (Related Products, Recently Viewed, homepage carousels) apply exactly
    // this to their track while sliding, which is what pushes the modal
    // off-center there. Since the modal has to stay inside .product-item
    // (the theme's existing variant/price-sync jQuery finds its card via
    // $(target).parents('.product-item')), we can't just move the modal to
    // <body>. Instead, temporarily neutralize the transform on any offending
    // ancestor while the modal is open, and restore it exactly on close.
    //
    // Deliberately NOT touching ancestor `overflow` here (an earlier version
    // did): a plain `overflow: hidden`/`auto` ancestor does not clip a fixed
    // descendant on its own — only in combination with one of the transform
    // properties above. Forcing it to `visible` was pure downside: on a
    // Slick carousel, `.slick-track`'s transform is what keeps its off-screen
    // slides shifted out of view, and `.slick-list`'s `overflow: hidden` is
    // what clips them. Neutralizing both at once un-clipped the *entire*
    // unsliced carousel row, which is exactly what was blowing out the page's
    // width on real phones (visible as "have to drag/zoom out to see it").
    // Leaving `.slick-list`'s overflow alone keeps those other slides clipped
    // exactly as before; only the transform needs neutralizing for the fixed
    // modal to center on the viewport.
    function releaseClippingAncestors(overlay) {
        var el = overlay.parentElement;

        while (el && el !== document.body && el !== document.documentElement) {
            var computed = window.getComputedStyle(el);
            var createsContainingBlock = (computed.transform && computed.transform !== 'none') ||
                (computed.perspective && computed.perspective !== 'none') ||
                (computed.filter && computed.filter !== 'none') ||
                (computed.willChange && computed.willChange.indexOf('transform') !== -1);

            if (createsContainingBlock) {
                releasedAncestors.push({
                    el: el,
                    transform: el.style.transform,
                    perspective: el.style.perspective,
                    filter: el.style.filter,
                    willChange: el.style.willChange
                });

                el.style.transform = 'none';
                el.style.perspective = 'none';
                el.style.filter = 'none';
                el.style.willChange = 'auto';
            }

            el = el.parentElement;
        }
    }

    function restoreClippingAncestors() {
        releasedAncestors.forEach(function (entry) {
            entry.el.style.transform = entry.transform;
            entry.el.style.perspective = entry.perspective;
            entry.el.style.filter = entry.filter;
            entry.el.style.willChange = entry.willChange;
        });
        releasedAncestors = [];
    }

    function openModal(trigger) {
        var overlay = getOverlay(trigger);
        if (!overlay) return;

        if (activeOverlay && activeOverlay !== overlay) {
            closeModal(activeOverlay);
        }

        lastTrigger = trigger;
        activeOverlay = overlay;

        releaseClippingAncestors(overlay);

        overlay.classList.add(ACTIVE_CLASS);
        overlay.removeAttribute('hidden');
        overlay.setAttribute('aria-hidden', 'false');
        trigger.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add(BODY_LOCK_CLASS);
        document.body.classList.add(BODY_LOCK_CLASS);

        var closeBtn = overlay.querySelector('[data-choose-options-close].choose-options-modal__close');
        if (closeBtn) {
            window.requestAnimationFrame(function () {
                closeBtn.focus();
            });
        }

        document.addEventListener('keydown', onKeydown);

        var modal = overlay.querySelector('.choose-options-modal');
        if (modal) updateUnitPriceLine(modal);
    }

    function closeModal(overlay) {
        if (!overlay) return;

        overlay.classList.remove(ACTIVE_CLASS);
        overlay.setAttribute('aria-hidden', 'true');

        window.setTimeout(function () {
            if (!overlay.classList.contains(ACTIVE_CLASS)) {
                overlay.setAttribute('hidden', 'hidden');
            }
        }, 260);

        var trigger = document.querySelector('[data-choose-options-target="' + overlay.id + '"]');
        if (trigger) {
            trigger.setAttribute('aria-expanded', 'false');
        }

        if (activeOverlay === overlay) {
            activeOverlay = null;
            document.documentElement.classList.remove(BODY_LOCK_CLASS);
            document.body.classList.remove(BODY_LOCK_CLASS);
            document.removeEventListener('keydown', onKeydown);
            restoreClippingAncestors();

            if (lastTrigger && typeof lastTrigger.focus === 'function') {
                lastTrigger.focus();
            }
            lastTrigger = null;
        }
    }

    function onKeydown(event) {
        if (!activeOverlay) return;

        if (event.key === 'Escape' || event.key === 'Esc') {
            event.preventDefault();
            closeModal(activeOverlay);
            return;
        }

        if (event.key === 'Tab') {
            trapFocus(event);
        }
    }

    function trapFocus(event) {
        var modal = activeOverlay.querySelector('.choose-options-modal');
        if (!modal) return;

        var focusable = Array.prototype.filter.call(
            modal.querySelectorAll(FOCUSABLE_SELECTOR),
            function (el) {
                return el.offsetParent !== null;
            }
        );

        if (!focusable.length) return;

        var first = focusable[0];
        var last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }

    document.addEventListener('click', function (event) {
        var trigger = event.target.closest('[data-choose-options-trigger]');
        if (trigger) {
            event.preventDefault();
            openModal(trigger);
            return;
        }

        var closer = event.target.closest('[data-choose-options-close]');
        if (closer) {
            event.preventDefault();
            closeModal(closer.closest('.choose-options-overlay'));
            return;
        }

        // Click on the dimmed backdrop itself (not the modal panel) also closes.
        if (event.target.classList && event.target.classList.contains('choose-options-overlay')) {
            closeModal(event.target);
        }
    });

    // After a successful "Add to cart" from inside the modal, close it shortly
    // after so the shopper sees the success state before the mini-cart takes over.
    document.addEventListener('click', function (event) {
        var addButton = event.target.closest('.choose-options-modal [data-btn-addtocart]');
        if (!addButton || addButton.hasAttribute('disabled')) return;

        var overlay = addButton.closest('.choose-options-overlay');
        if (!overlay) return;

        window.setTimeout(function () {
            if (overlay === activeOverlay) {
                closeModal(overlay);
            }
        }, 1100);
    });

    // --- Per-unit price + bulk-savings line -----------------------------
    // On the product page this updates for free: the price block is
    // server-refetched whole on every variant change (variants.js'
    // Section Rendering API call), so snippets/product-unit-price.liquid's
    // output just comes along with it. This modal never refetches from the
    // server, so it needs the same "quantity from the variant title, compare
    // to the qty-1 variant" arithmetic mirrored here client-side, using the
    // variant JSON already sitting on the card (.product-item[data-json-product]).

    function parseUnitQuantity(title) {
        if (!title) return 1;
        var parts = title.trim().split(' ');
        var quantity = parseInt(parts[parts.length - 1], 10);
        return isNaN(quantity) || quantity < 1 ? 1 : quantity;
    }

    function findSelectedVariant(modal, variants) {
        var groups = modal.querySelectorAll('.choose-options-variants [data-option-index]');
        if (!groups.length) return null;

        var selected = [];
        groups.forEach(function (group) {
            var index = parseInt(group.getAttribute('data-option-index'), 10);
            var checked = group.querySelector('.single-option:checked');
            if (checked) selected[index] = checked.value;
        });

        return variants.find(function (variant) {
            return selected.every(function (value, index) {
                return value === undefined || variant.options[index] === value;
            });
        }) || null;
    }

    function updateUnitPriceLine(modal) {
        var priceWrap = modal.querySelector('.choose-options-modal__price');
        var eachEl = priceWrap && priceWrap.querySelector('[data-unit-price-each]');
        var savingsEl = priceWrap && priceWrap.querySelector('[data-unit-price-savings]');
        var productItem = modal.closest('.product-item');

        if (!priceWrap || !productItem || !productItem.dataset.jsonProduct) return;

        var productData;
        try {
            productData = JSON.parse(productItem.dataset.jsonProduct);
        } catch (e) {
            return;
        }

        var variants = productData.variants || [];
        var variant = findSelectedVariant(modal, variants);
        var quantity = variant ? parseUnitQuantity(variant.title) : 1;

        if (!variant || quantity <= 1) {
            if (eachEl) eachEl.style.display = 'none';
            if (savingsEl) savingsEl.style.display = 'none';
            return;
        }

        // .unit-price-each and .unit-price-savings are direct siblings of the
        // price block (not nested in a wrapper) so component-choose-options.css
        // can lay .choose-options-modal__price out as a flex row: the per-piece
        // price sits inline next to the price, savings wraps onto its own line.
        if (!eachEl) {
            eachEl = document.createElement('span');
            eachEl.className = 'unit-price-each';
            eachEl.setAttribute('data-unit-price-each', '');
            priceWrap.appendChild(eachEl);
        }
        if (!savingsEl) {
            savingsEl = document.createElement('span');
            savingsEl.className = 'unit-price-savings';
            savingsEl.setAttribute('data-unit-price-savings', '');
            priceWrap.appendChild(savingsEl);
        }

        var unitPrice = Math.round(variant.price / quantity);
        var baseline = variants.find(function (v) {
            return parseUnitQuantity(v.title) === 1;
        });

        eachEl.style.display = '';
        eachEl.textContent = '(' + Shopify.formatMoney(unitPrice, window.money_format) + ' ' + window.variantStrings.perPiece + ')';

        var savingsPercent = baseline && baseline.price > unitPrice
            ? Math.round(((baseline.price - unitPrice) / baseline.price) * 100)
            : 0;

        if (savingsPercent > 0) {
            savingsEl.textContent = window.variantStrings.unitPriceSavings.replace('__PERCENT__', savingsPercent);
            savingsEl.style.display = '';
        } else {
            savingsEl.style.display = 'none';
        }
    }

    document.addEventListener('change', function (event) {
        if (!event.target.matches('.choose-options-modal .single-option')) return;

        var modal = event.target.closest('.choose-options-modal');
        if (modal) updateUnitPriceLine(modal);
    });
})();
